/* eslint-disable max-len */
import Checkbox from '@mui/material/Checkbox';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { SelectOption } from '../../basics/options/options.types';
import { checkboxStyle } from './CepFormTargetListGroup.styles';
import { VeevaSyncStatusBadgeMap } from 'basics/constants/common.constant';
import { CepStatus, TargetListMode, VeevaSyncStatus } from 'basics/enums/maya.enums';
import { CepStateType } from 'basics/types/maya.types';
import { CepFormTargetListGroupConfigType } from 'groups/CepFormTargetListGroup/CepFormTargetListGroup.types';
import cepState from 'states/cep.states';
import Tag from 'yoda-ui/components/Tags/Tag';
import { YodaBorderRadius, YodaFontSize, YodaFontWeight, YodaSpacing } from 'yoda-ui/yodaTheme';

const useCepFormTargetListGroupConfig = (
  targetListMode: TargetListMode,
  selectTargetListOnChange: (event: ChangeEvent<HTMLInputElement>, value: string) => void,
) => {
  const { t } = useTranslation();
  const flags = useFlags();

  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const isCepApproved = cepStateValue.cep?.status === CepStatus.APPROVED;
  const shouldBeDisabled = isCepApproved
    && (!flags.mayaBdcfcsd653ExtendEditingOfApprovedCep || !flags.mayaBdcfcsd1251EnableEditingOfTargetListOfApprovedCep);
  const [listOfSelectedMdmId, setListOfSelectedMdmId] = useState<string[]>([]);

  const handleCheckboxChange = (mdmId: string, checked: boolean) => {
    setListOfSelectedMdmId((prev) => {
      if (checked) {
        return [...new Set([...prev, mdmId])];
      }

      return prev.filter((id) => id !== mdmId);
    });
  };

  const veevaSyncStatusOptions: SelectOption[] = [
    { id: VeevaSyncStatus.PENDING, label: VeevaSyncStatus.PENDING },
    ...(flags.mayaAkma1292DisableAutoTargetSync?.disabled
      ? [{ id: VeevaSyncStatus.PENDING_DELETION, label: VeevaSyncStatus.PENDING_DELETION }]
      : []),
    { id: VeevaSyncStatus.SYNC, label: VeevaSyncStatus.SYNC },
    { id: VeevaSyncStatus.ERROR, label: VeevaSyncStatus.ERROR },
    { id: 'All', label: t('maya_cep_form_target_list_status_filter_option_show_all') },
  ];

  const targetSelectRadioConfigs = [
    {
      label: t('maya_targetlist_select_dynamic'),
      key: 'dynamic_list',
      value: TargetListMode.dynamic,
      checked: targetListMode === TargetListMode.dynamic,
      disabled: shouldBeDisabled || !flags.mayaBiogenlinc4307EnableDynamicTargeting,
    },
    {
      label: t('maya_targetlist_select_csv'),
      key: 'csv_list',
      value: TargetListMode.upload,
      checked: targetListMode === TargetListMode.upload,
      disabled: shouldBeDisabled,
    },
  ];

  const cepFormTargetListGroupConfig: CepFormTargetListGroupConfigType = {
    selectTargetList: {
      name: 'selectTargetList',
      radioConfigs: targetSelectRadioConfigs,
      row: true,
      defaultValue: targetListMode,
      onChange: selectTargetListOnChange,
    },
  };

  const syncStatusTargetListFilterConfig = {
    name: 'status',
    placeholder: t('maya_cep_form_target_list_status_filter_placeholder'),
    options: veevaSyncStatusOptions,
    defaultValue: veevaSyncStatusOptions.find((option) => option.id === 'All')?.id,
    required: false,
    disableClearable: true,
  };

  const targetListConfig: GridColDef[] = [
    {
      field: 'mdmId',
      headerName: t('maya_targetlist_mdmid_label'),
      flex: 0.2,
      sortable: false,
      cellClassName: 'gridlist-cell-bold',
    },
    {
      field: 'email',
      headerName: t('maya_targetlist_email_label'),
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'firstName',
      headerName: t('maya_targetlist_firstname_label'),
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'lastName',
      headerName: t('maya_targetlist_lastname_label'),
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'veevaSyncStatus',
      headerName: t('maya_veeva_sync_status_header_label'),
      flex: 0.2,
      minWidth: 150,
      renderCell: (cellValues: GridRenderCellParams) => {
        const badge = VeevaSyncStatusBadgeMap[cellValues.row.veevaSyncStatus || VeevaSyncStatus.PENDING];
        return badge && (
          <Tag
            fitContent
            bgcolor={ badge.bgColor }
            color={ badge.color }
            fontSize={ YodaFontSize.small }
            fontWeight={ YodaFontWeight.medium }
            paddingY={ YodaSpacing.xxxxSmall }
            paddingX={ YodaSpacing.xSmall }
            width={ 'auto' }
            height={ '20px' }
            borderRadius={ YodaBorderRadius.xSmall }
          >
            { badge.label }
          </Tag>
        );
      },
      sortable: false,
    },
    {
      field: 'checkbox',
      headerName: '',
      renderCell: (params: GridRenderCellParams) => {
        const target = params.row;
        return (
          <Checkbox
            checked={ listOfSelectedMdmId.includes(target.mdmId) }
            onChange={ (event: ChangeEvent<HTMLInputElement>) => handleCheckboxChange(target.mdmId, event.target.checked) }
            sx={ checkboxStyle }
          />
        );
      },
      sortable: false,
      flex: 0.1,
      hide: !flags.mayaAkma507EnableTargetsSelection,
    },
  ];

  return {
    cepFormTargetListGroupConfig,
    targetListConfig,
    listOfSelectedMdmId,
    setListOfSelectedMdmId,
    syncStatusTargetListFilterConfig,
  };
};

export default useCepFormTargetListGroupConfig;
